import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  useColorScheme,
  Accordion,
  AccordionSummary,
  Button,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import MainCategoriesGrid from './MainCategoriesGrid';
import { getAllCategories } from './actions';
import { CategoryViewModel } from '../../configuration/api/apiSchema';
import { HandymanAppState } from '../../configuration/redux/reducers';
import {
  GlassContainer,
  GlassCard,
  SectionHeader,
  GlassColors,
  BodyText,
  GlassButton,
  ButtonTertiary,
} from '../../components/glassmorphism/GlassmorphismStyles';

import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RailStepper from '../../components/RailStepper/RailStepper';
import { getCategoryIcon } from '../../configuration/categoryConfigIcon';
import SelectCategoriesSkeleton from './skeletons/SelectCategoriesSkeleton';

export interface Category {
  key: string;
  name: string;
  subcategories: Category[];
}

interface PassedProps {
  onSubmit: (categoryKeys: string[]) => void;
  isLoading: boolean;
}

const CategoriesList: React.FC<PassedProps> = ({ onSubmit, isLoading }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Get categories from Redux store
  const { allCategories, isLoading: isCategoriesLoading } = useSelector(
    (state: HandymanAppState) => state.category,
  );

  // const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [selectedSubcategories, setSelectedSubcategories] = useState<
    Category[]
  >([]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Transform CategoryViewModel[] to Category[] for compatibility
  const transformCategories = useCallback(
    (categories: CategoryViewModel[] | undefined): Category[] => {
      if (!categories) return [];
      return categories.map((cat) => ({
        key: cat.key || '',
        name: cat.name || '',
        subcategories: transformCategories(cat.subcategories || []),
      }));
    },
    [],
  );

  const categories = useMemo(
    () => transformCategories(allCategories),
    [allCategories, transformCategories],
  );

  // Load all categories from backend on mount
  useEffect(() => {
    if (!allCategories && !isCategoriesLoading) {
      dispatch(getAllCategories() as any);
    }
  }, [allCategories, isCategoriesLoading]); // Remove dispatch from dependencies

  // -- UPDATED FILTERING FOR ROOT CATEGORIES --
  // const filteredCategories = categories.filter((cat) => {
  //   if (!searchTerm) return true; // If no search term, show all
  //   const lowerTerm = searchTerm.toLowerCase();

  //   // Check if category name includes search term
  //   const catNameMatches = cat.name.toLowerCase().includes(lowerTerm);

  //   // Check if any subcategory name includes search term
  //   const subcatMatches = cat.subcategories?.some((sub) =>
  //     sub.name.toLowerCase().includes(lowerTerm)
  //   );

  //   return catNameMatches || subcatMatches;
  // });

  // -- SUBCATEGORY FILTERING (unchanged) --
  // const filteredSubcategories = selectedCategory
  //   ? categories
  //       .find((cat) => cat.key === selectedCategory.key)
  //       ?.subcategories.filter((sub) =>
  //         sub.name.toLowerCase().includes(searchTerm.toLowerCase())
  //       ) ?? []
  //   : [];

  /** When a user clicks a main category */
  const handleSelectCategory = (category: Category) => {
    setSelectedCategory(category);
    // setSearchTerm('');
    setSelectedSubcategories([]);

    // Scroll to the top for a smoother UX
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  /** Check/uncheck a subcategory */
  // const handleSubcategoryChange = (
  //   subcategory: Category,
  //   isChecked: boolean
  // ) => {
  //   setSelectedSubcategories((prev) =>
  //     isChecked
  //       ? [...prev, subcategory]
  //       : prev.filter((item) => item !== subcategory)
  //   );
  // };

  /** Check/uncheck all subcategories */
  // const handleSelectAll = (selectAll: boolean) => {
  //   if (selectedCategory) {
  //     const foundCat = categories.find(
  //       (cat) => cat.key === selectedCategory.key
  //     );
  //     if (foundCat) {
  //       setSelectedSubcategories(selectAll ? foundCat.subcategories : []);
  //     }
  //   }
  // };

  // Toggle item checkbox (last-level only)
  const toggleKey = (key: string, checked: boolean) => {
    setSelectedKeys((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
  };
  /** Final Submit -> includes the main category key + selected subcategories' keys */
  const handleSubmit = () => {
    if (!selectedCategory) return;

    const selectedCat =
      categories.find((c) => c.key === selectedCategory.key) ??
      selectedCategory;

    const keys = new Set<string>();

    // root
    keys.add(selectedCat.key);

    // items
    selectedKeys.forEach((k) => keys.add(k));

    selectedCat.subcategories.forEach((group) => {
      const groupHasSelected = (group.subcategories ?? []).some((item) =>
        selectedKeys.includes(item.key),
      );
      if (groupHasSelected) keys.add(group.key);
    });

    onSubmit(Array.from(keys));
  };

  // Show loading indicator while categories are being fetched
  if (isCategoriesLoading && categories.length === 0) {
    return (
      <GlassContainer
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        {/* <CircularProgress size={60} /> */}
        <SelectCategoriesSkeleton />
      </GlassContainer>
    );
  }

  const selectedCategoryFromList = selectedCategory
    ? (categories.find((c) => c.key === selectedCategory.key) ??
      selectedCategory)
    : null;

  return (
    <GlassContainer
      sx={{
        display: 'flex',
        // alignItems: 'center',
        p: { xs: 0, sm: '12px', tablet: '32px' },
      }}
    >
      <GlassCard
        sx={{
          width: '100%',
          maxWidth: '956px',
          mx: 'auto',
          padding: { xs: '24px 12px', tablet: '32px' },
          [theme.breakpoints.down('sm')]: {
            boxShadow: 'unset',
          },
        }}
      >
        <SectionHeader
          variant='h4'
          sx={{
            textAlign: 'center',
            fontSize: { xs: '20px', sm: '24px' },
            lineHeight: { xs: '160%', sm: '133%' },
            letterSpacing: { xs: '.15px', sm: 0 },
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: { xs: 800, sm: 700 },
            filter: 'drop-shadow(0px -0.25px 0.4px #0000008C)',
            color: isDark
              ? GlassColors.dark.accent
              : GlassColors.light.secondary,
            mb: '8px',
          }}
        >
          Choose Your Services
        </SectionHeader>

        <BodyText
          sx={{
            textAlign: 'center',
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '157%', sm: '175%' },
            letterSpacing: { xs: '.1px', sm: '.15px' },

            // maxWidth: '304px',
            mx: 'auto',
            mb: {
              xs: '16px',
              sm: '24px',
            },
          }}
        >
          Select all services you'd like to offer. You can always update these
          later in your profile
        </BodyText>

        {/* Search Field 
        <TextField
          fullWidth
          variant='outlined'
          placeholder={
            selectedCategory
              ? 'Search for a subcategory'
              : 'Search for a category'
          }
          value={searchTerm}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          sx={{ mb: 3 }}
        />*/}

        {selectedCategoryFromList ? (
          <>
            <Box
              sx={{
                borderRadius: '32px',
                boxShadow: {
                  xs: `
                  -2.82px -2.82px 8.49px 0px rgba(255, 255, 255, 0.3),
                  14.15px 14.15px 22.64px -5.37px rgba(0, 0, 0, 0.1),
                  -7.54px -7.54px 8.66px 0px rgba(255, 255, 255, 0.2),
                  5px 5px 24px -11.7px rgba(0, 0, 0, 0.2)
                `,
                  tablet: 'none',
                },
                mb: {
                  xs: '8px',

                  tablet: '16px',
                },
              }}
            >
              <GlassCard
                sx={{
                  borderRadius: '32px',
                  p: '24px',
                  maxWidth: '760px',
                  m: '0 auto',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ButtonTertiary
                    sx={{
                      fontSize: '12px',
                      display: { xs: 'none', tablet: 'flex' },
                    }}
                    variant='outlined'
                    color='primary'
                    onClick={() => {
                      setSelectedCategory(null);
                      setSelectedKeys([]);
                    }}
                    disabled={isLoading}
                  >
                    <ArrowBackIosIcon
                      sx={{
                        fontSize: '14px',
                        '&&': {
                          fill: 'currentColor',
                        },
                        '&& path': {
                          fill: 'currentColor',
                        },
                      }}
                    />
                    Back
                  </ButtonTertiary>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', m: '0 auto' }}
                  >
                    <Box
                      sx={{
                        '&': {
                          width: { xs: '32px', tablet: '48px' },
                          height: { xs: '32px', tablet: '48px' },
                          mr: '16px',
                        },
                      }}
                    >
                      {getCategoryIcon(selectedCategoryFromList.key)}{' '}
                    </Box>
                    <Typography
                      variant='h5'
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '16px', tablet: '24px' },
                        LineHeight: { xs: '175%', tablet: '133%' },
                        color: isDark
                          ? GlassColors.dark.textSecondary
                          : GlassColors.light.textSecondary,
                      }}
                    >
                      {selectedCategoryFromList.name}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: { xs: '16px', tablet: '32px' },
                    p: '16px 12px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '10px',
                    borderRadius: '32px',
                    boxShadow: `
                      0px 0px 10px 0px #0000000A,
                      0px -0.5px 0.25px 0px #0000004D,
                      0px 0.5px 0.75px 0px #FFFFFF,
                      0px 2.07px 3.73px 0px #00000045 inset,
                      0px 1.08px 2.48px 0px #00000040 inset
                    `,
                  }}
                >
                  {selectedCategoryFromList.subcategories.map((group) => {
                    const hasChildren = (group.subcategories?.length ?? 0) > 0;

                    return (
                      <Accordion
                        key={group.key}
                        disableGutters
                        elevation={0}
                        sx={{
                          '&.MuiPaper-root': {
                            border: 0,
                            p: 0,
                            background: 'none',
                            maxWidth: '220px',
                          },
                          '&.MuiPaper-root.Mui-expanded': {
                            width: '100%',
                            maxWidth: 'none',
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={
                            hasChildren ? <ArrowForwardIosIcon /> : null
                          }
                          sx={{
                            p: '0 0 0 32px',
                            minHeight: 'unset',
                            '&: hover': {
                              backgroundColor: 'transparent',
                            },
                            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded':
                              {
                                transform: 'rotate(90deg)',
                              },
                            '& .MuiAccordionSummary-expandIconWrapper': {
                              position: 'absolute',
                              left: 0,
                              top: '4px',
                              transition: 'transform .2s',
                            },
                            '& .MuiAccordionSummary-content': {
                              m: 0,
                            },
                            '& svg': {
                              fontSize: '20px',
                              opacity: 1,
                              fill: isDark
                                ? GlassColors.dark.textSecondary
                                : GlassColors.light.textSecondary,
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'roboto, sans-serif',
                              fontWeight: 700,
                              fontSize: '16px',
                              lineHeight: '175%',
                              letterSpacing: '.15px',
                              color: isDark
                                ? GlassColors.dark.textSecondary
                                : GlassColors.light.textSecondary,
                            }}
                          >
                            {group.name}
                          </Typography>
                        </AccordionSummary>

                        {hasChildren && (
                          <AccordionDetails sx={{ p: '4px 0 16px 0 ' }}>
                            <FormGroup sx={{ flexDirection: 'row' }}>
                              {group.subcategories.map((item) => (
                                <FormControlLabel
                                  key={item.key}
                                  sx={{
                                    m: 0,
                                    pr: '10px',
                                    width: '224px',
                                    maxWidth: '100%',
                                    '& .MuiTypography-root': {
                                      fontFamily: 'roboto, sans-serif',
                                      fontWeight: 700,
                                      fontSize: '14px',
                                      lineHeight: '157%',
                                      letterSpacing: '.1px',
                                      color: isDark
                                        ? GlassColors.dark.textSecondary
                                        : GlassColors.light.textSecondary,
                                    },
                                  }}
                                  control={
                                    <Checkbox
                                      checked={selectedKeys.includes(item.key)}
                                      onChange={(e) =>
                                        toggleKey(item.key, e.target.checked)
                                      }
                                    />
                                  }
                                  label={item.name}
                                />
                              ))}
                            </FormGroup>
                          </AccordionDetails>
                        )}
                      </Accordion>
                    );
                  })}
                </Box>
                <ButtonTertiary
                  sx={{
                    fontSize: '12px',
                    m: '16px auto 0',
                    display: { xs: 'flex', tablet: 'none' },
                  }}
                  variant='outlined'
                  color='primary'
                  onClick={() => {
                    setSelectedCategory(null);
                    setSelectedKeys([]);
                  }}
                  disabled={isLoading}
                >
                  <ArrowBackIosIcon
                    sx={{
                      fontSize: '14px',
                      '&&': {
                        fill: 'currentColor',
                      },
                      '&& path': {
                        fill: 'currentColor',
                      },
                    }}
                  />
                  Back
                </ButtonTertiary>
              </GlassCard>
            </Box>
            <Box sx={{ maxWidth: '760px', mx: 'auto' }}>
              <RailStepper activeIndex={3} isProviderFlow={true} />
            </Box>
          </>
        ) : (
          <MainCategoriesGrid
            filteredCategories={categories}
            handleSelectCategory={handleSelectCategory}
          />
        )}
        {/* Buttons */}
        <Box
          sx={{
            mt: { xs: '8px', tablet: '12px' },
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <GlassButton
            variant='contained'
            onClick={handleSubmit}
            disabled={isLoading || !selectedKeys?.length}
            sx={{ minWidth: 100, fontSize: '12px', ml: 'auto' }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Create account'
            )}
          </GlassButton>
        </Box>
      </GlassCard>
    </GlassContainer>
  );
};

export default CategoriesList;
